import { createContext, useContext } from 'react'
import type { LandMission, LandMissionAction } from '../api/landApi'

export type PlayableLandMission = LandMission & {
  /**
   * Land remains publicly playable in the current release. `locked` is server
   * metadata for a future policy and must not be used as a client-side gate.
   */
  playable: true
}

export type LandSyncStatus = 'idle' | 'syncing' | 'synced' | 'local' | 'degraded'

export type LandSyncErrorCode =
  | 'mission-not-loaded'
  | 'unauthorized'
  | 'forbidden'
  | 'conflict'
  | 'unavailable'
  | 'network'

export type LandSyncError = {
  code: LandSyncErrorCode
  message: string
  missionCode?: string
}

export type LandProgressContextValue = {
  missions: PlayableLandMission[]
  progress: LandMissionAction[]
  syncStatus: LandSyncStatus
  error: LandSyncError | null
  refresh: () => Promise<void>
  startMission: (missionCode: string) => Promise<LandMissionAction | null>
  completeMission: (missionCode: string) => Promise<LandMissionAction | null>
  clearError: () => void
}

export const LandProgressContext = createContext<LandProgressContextValue | null>(null)

export function useLandProgress() {
  const value = useContext(LandProgressContext)
  if (!value) throw new Error('useLandProgress must be used inside LandProgressProvider')
  return value
}
